/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AiOutlineUser } from 'react-icons/ai';
import { FiDownload, FiUserPlus } from 'react-icons/fi';
import { useGetQuickSellerSummaryQuery, useGetSellerSummaryXlsxMutation } from '../store';
import FilterTable, { BaseData } from './Table/Table';
import { SellerSimpleSummaryDTO } from '../models/order';
import Button from './atoms/Button';
import { formatCurrency } from '../utils/shared';
import EmptyTable from './Table/EmptyTable';
import useGetUserRole from '../hooks/useGetUserRole';
import MenuDropdown from './atoms/MenuDropdown';
import CustomModal from './atoms/CustomModal';
import DeleteSeller from './DeleteSeller';
import AddEditSellerToEvent from './AddEditSellerToEvent';
import AddUserExpense from './AddUserExpense';
import { EventTransactionType } from '../models/event-transaction';
import OrdersTableSkeleton from './OrdersTableSkeleton';
// import OrdersTableSkeleton from './OrdersTableSkeleton';

interface Props {
  businessId: string;
  eventId: string;
  handleAddSeller: () => void;
}

interface TableSellerItem extends BaseData {
  id: string;
  username: string | JSX.Element;
  active: JSX.Element;
  total_items_count: number;
  total_amount: number | JSX.Element;
  commission: string | JSX.Element;
  expenses: number | JSX.Element;
  balance: number | JSX.Element;
  total_commision: number | JSX.Element;
}

const SellersTable: React.FC<Props> = ({ businessId, eventId, handleAddSeller }) => {
  const navigate = useNavigate();
  const role = useGetUserRole();
  const [getSellerSummaryXlsx, resultsSellerSummaryXlsx] = useGetSellerSummaryXlsxMutation();
  const { data: sellerSummary, isFetching: isSellerSummaryLoading, refetch: getSellerSummary } = useGetQuickSellerSummaryQuery({ businessId, eventId });
  const [filteredSellerSummary, setFilteredSellerSummary] = useState<SellerSimpleSummaryDTO[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'edit' | 'movement'>('delete');
  const [selectedRow, setSelectedRow] = useState<SellerSimpleSummaryDTO | null>(null);

  useEffect(() => {
    getSellerSummary();
  }, [getSellerSummary, businessId, eventId]);

  useEffect(() => {
    if (sellerSummary) {
      setFilteredSellerSummary(sellerSummary.filter((user) => user.permissions === 'SELLER' || user.permissions === 'ADMIN'));
    }
  }, [sellerSummary]);

  const handleViewDetails = (row: TableSellerItem) => {
    navigate(`${sellerSummary?.find((user) => user.user_id === row.id)?.user_id}/details`);
  };

  const handleEditSeller = (row: TableSellerItem) => {
    setSelectedRow(sellerSummary?.find((user) => user.user_id === row.id) as SellerSimpleSummaryDTO);
    setModalType('edit');
    setIsModalOpen(true);
  };

  const handleRegisterMovement = (row: TableSellerItem) => {
    setSelectedRow(sellerSummary?.find((user) => user.user_id === row.id) as SellerSimpleSummaryDTO);
    setModalType('movement');
    setIsModalOpen(true);
  };

  const handleDeleteSeller = (row: TableSellerItem) => {
    setSelectedRow(sellerSummary?.find((user) => user.user_id === row.id) as SellerSimpleSummaryDTO);
    setModalType('delete');
    setIsModalOpen(true);
  };

  const handleDownload = () => {
    getSellerSummaryXlsx({ businessId, eventId });
  };

  const hasGroupData = filteredSellerSummary?.some((item) => item.group != null && item.group !== '');

  const headCells = [
    {
      id: 'username',
      label: 'Usuario',
    },
    {
      id: 'active',
      label: 'Estado',
    },
    {
      id: 'tag',
      label: 'Etiqueta',
    },
    ...(hasGroupData ? [{ id: 'group' as const, label: 'Nivel' }] : []),
    {
      id: 'total_items_count',
      label: 'Cantidad de ventas',
    },
    {
      id: 'items_scanned',
      label: 'QRs escaneados',
    },
    {
      id: 'total_amount_online',
      label: 'Total manual',
    },
    {
      id: 'total_amount_referral',
      label: 'Total link',
    },
    {
      id: 'total_amount',
      label: 'Monto total',
    },
    {
      id: 'commission',
      label: 'Comisión',
    },
    {
      id: 'total_commision',
      label: 'Comisión total',
    },
    {
      id: 'expenses',
      label: 'Gastos',
    },

    {
      id: 'balance',
      label: 'Saldo',
    },
  ];

  const tableRows: TableSellerItem[] = filteredSellerSummary?.map((item: SellerSimpleSummaryDTO) => ({
    id: item.user_id || '',
    active: item.active || role === 'ADMIN'
      ? <div className='tag-primary'>
        Activo
      </div> : <div className='tag-warning'>
        Inactivo
      </div>,
    tag: <div className='whitespace-nowrap'>{item.tag || '-'}</div>,
    username: (
      <Link to={`${item.user_id}/details`} className="text-primary-500 hover:underline whitespace-nowrap">
        {item.username || ''}
      </Link>
    ),
    commission: item.commission
      ? <div className='whitespace-nowrap'>{item.commission * 100}%</div>
      : '-',
    total_items_count: item?.total_items_count || 0,
    total_amount_online: item.total_amount_online ? <div className='whitespace-nowrap'>{formatCurrency(item.total_amount_online, 'no-decimals')}</div> : <div className='whitespace-nowrap'>$0</div>,
    total_amount_referral: item.total_amount_referral ? <div className='whitespace-nowrap'>{formatCurrency(item.total_amount_referral, 'no-decimals')}</div> : <div className='whitespace-nowrap'>$0</div>,
    total_amount: item?.total_amount ? <div className='whitespace-nowrap'>{formatCurrency(item.total_amount, 'no-decimals')}</div> : <div className='whitespace-nowrap'>$0</div>,
    total_commision: item.total_commision ? <div className='whitespace-nowrap'>{formatCurrency(item.total_commision, 'no-decimals')}</div> : <div className='whitespace-nowrap'>$0</div>,
    expenses: item.expenses ? <div className='whitespace-nowrap'>{formatCurrency(item.expenses, 'no-decimals')}</div> : <div className='whitespace-nowrap'>$0</div>,
    balance: item?.balance ? <div className='whitespace-nowrap'>{formatCurrency(item.balance, 'no-decimals')}</div> : <div className='whitespace-nowrap'>$0</div>,
    items_scanned: item?.items_scanned || 0,
    group: item.group || '-',
  })) || [];

  const actions = (row: TableSellerItem) => (
    <MenuDropdown
      data={row}
      options={[
        {
          label: 'Ver detalles',
          onClick: () => handleViewDetails(row),
        },
        {
          label: 'Editar vendedor',
          onClick: () => handleEditSeller(row),
        },
        {
          label: 'Registrar movimiento',
          onClick: () => handleRegisterMovement(row),
        },
        {
          label: 'Eliminar vendedor',
          onClick: () => handleDeleteSeller(row),
        },
      ]}
    />
  );

  const customButton = [<Button onClick={() => handleAddSeller()} variant="secondary">
    <div className='flex flex-row gap-2 items-center'>
      <FiUserPlus className='text-xl' />
      <p className='whitespace-nowrap hidden md:block'>Agregar vendedor</p>
    </div>
  </Button>,
  <Button onClick={() => handleDownload()} variant="secondary" loading={resultsSellerSummaryXlsx.isLoading}>
    <div className='flex flex-row gap-2 items-center'>
      <FiDownload className='text-xl' />
      <p className='whitespace-nowrap hidden md:block'>Descargar</p>
    </div>
  </Button>];

  const getTags = () => Array.from(new Set(filteredSellerSummary?.map((item) => item.tag) || [])) as string[];

  return (
    <div>
      {(tableRows.length > 0 || isSellerSummaryLoading) ? (
        isSellerSummaryLoading ? <OrdersTableSkeleton /> : <FilterTable
          rows={tableRows}
          headCells={headCells}
          actions={actions}
          customButtons={customButton}
          pagination={{
            pageSize: 30,
            enabled: true,
          }}
          searchEnabled={true}
          tableId="sellers-table"
        />
      ) : (
        <EmptyTable
          title="Aún no hay vendedores en este evento"
          info="Aquí aparecerán todos los vendedores que crees en este evento"
          icon={AiOutlineUser}
          iconColor="text-secondary-500"
          button={[{
            label: 'Agregar vendedor',
            onClick: handleAddSeller,
            variant: 'secondary',
            icon: FiUserPlus,
          }]}
        />
      )}
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
      >
        {modalType === 'delete' && <DeleteSeller
          sellsCount={selectedRow?.total_items_count || 0}
          userEventId={selectedRow?.user_event_id || ''}
          businessId={businessId}
          eventId={eventId}
          onCancel={() => setIsModalOpen(false)}
          onDeleteSuccess={() => {
            setIsModalOpen(false);
          }}
        />}
        {modalType === 'edit' && <div>
          <div className="flex flex-col gap-2 mb-2">
            <h1 className="text-xl font-medium">Editar vendedor</h1>
            <p className="text-text-tertiary text-sm">
              Podrás configurar la comisión de este vendedor
            </p>
          </div>
          <AddEditSellerToEvent
            businessId={businessId}
            eventId={eventId}
            onClose={() => setIsModalOpen(false)}
            onSuccess={() => {
              setIsModalOpen(false);
            }}
            sellerToEdit={{
              userId: selectedRow?.user_event_id || '',
              comissionRate: Number(selectedRow?.commission) || 0,
              active: selectedRow?.active || false,
              email: selectedRow?.email || '',
              alias: selectedRow?.tag || undefined,
              tag: selectedRow?.alias || undefined,
              tags: getTags() || [],
            }}
          />
        </div>}
        {modalType === 'movement' && <div>
          <div className="flex flex-col gap-2 mb-2">
            <h1 className="text-xl font-medium">Registrar movimiento</h1>
            <p className="text-text-tertiary text-sm">
              Podrás agregar gastos a este vendedor o registrar un ingreso
            </p>
          </div>
          <AddUserExpense
            businessId={businessId}
            eventId={eventId}
            sellerId={selectedRow?.user_id || ''}
            onSuccess={() => setIsModalOpen(false)}
            type={EventTransactionType.SellerExpense}
          />
        </div>}
      </CustomModal>
    </div>

  );
};

export default SellersTable;
